import {
  arrayRemove,
  arrayUnion,
  doc,
  getDoc,
  runTransaction,
} from 'firebase/firestore';
import {
  assertValidVerseTuple,
  dedupeVerseTuples,
  verseKeyToTuple,
  verseTupleToKey,
} from './canonical_verse_utils';

export const MEMORY_RECALL_DELAY_MS = 30000;

export const MEMORY_RATING_MULTIPLIERS = Object.freeze({
  hard: 1,
  okay: 1.25,
  easy: 1.5,
});

export const MEMORY_LEARNING_STAGES = Object.freeze([
  'pray',
  'blank_25',
  'blank_50',
  'blank_75',
  'initials',
  'confirm',
]);

const READ_TIMEOUT_MS = 7000;
const READ_RETRIES = 3;

async function readMemoryDataWithRetry(operation, retries = READ_RETRIES) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      let timeoutId;
      try {
        return await Promise.race([
          operation(),
          new Promise((_, reject) => {
            timeoutId = setTimeout(
              () => reject(new Error('Memorization request timed out')),
              READ_TIMEOUT_MS
            );
          }),
        ]);
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await new Promise(resolve => {
          setTimeout(resolve, 1000 * (2 ** attempt));
        });
      }
    }
  }
  throw lastError;
}

function normalizeTimestamp(value) {
  return Number.isFinite(value) ? value : null;
}

function validateNow(now) {
  if (!Number.isFinite(now)) {
    throw new TypeError('Current time must be milliseconds');
  }
}

function getMemoryForKey(statusByVerseKey, verseKey) {
  if (statusByVerseKey instanceof Map) {
    return statusByVerseKey.get(verseKey);
  }
  return statusByVerseKey?.[verseKey] || statusByVerseKey?.[String(verseKey)];
}

function createQueueCard(
  tuple,
  memory,
  mode,
  availableAt,
  scheduledFor = null,
  isRelearning = false
) {
  return {
    verse_key: verseTupleToKey(tuple),
    tuple: [...tuple],
    mode,
    available_at: availableAt,
    scheduled_for: scheduledFor,
    is_relearning: isRelearning,
    memory,
  };
}

function compareQueueCards(first, second) {
  return first.available_at - second.available_at;
}

export function getMemorizationIndexId(bookNumber) {
  if (!Number.isInteger(bookNumber) || bookNumber < 1 || bookNumber > 66) {
    throw new RangeError(`Invalid canonical book number: ${bookNumber}`);
  }
  return String(bookNumber).padStart(2, '0');
}

export function normalizeMemory(tuple, memory = {}, now = Date.now()) {
  assertValidVerseTuple(tuple);
  validateNow(now);
  const [bookNumber, chapter, verse] = tuple;
  return {
    verse_key: verseTupleToKey(tuple),
    book_number: bookNumber,
    chapter,
    verse,
    learned: memory?.learned === true,
    last_correct_at: normalizeTimestamp(memory?.last_correct_at),
    last_forgot_at: normalizeTimestamp(memory?.last_forgot_at),
    forget_at: normalizeTimestamp(memory?.forget_at),
    correct_count: (
      Number.isInteger(memory?.correct_count) && memory.correct_count >= 0
    ) ? memory.correct_count : 0,
    updated_at: normalizeTimestamp(memory?.updated_at) ?? now,
  };
}

export function isPendingRelearning(memory) {
  return memory?.relearning === true || (
    memory?.learned === false
    && normalizeTimestamp(memory?.last_correct_at) !== null
  );
}

export function buildMemoryStatusMap(indexDocuments, now = Date.now()) {
  validateNow(now);
  const statusByVerseKey = new Map();
  indexDocuments.forEach(indexDocument => {
    const data = typeof indexDocument?.data === 'function'
      ? indexDocument.data()
      : indexDocument;
    const learnedVerseKeys = Array.isArray(data?.learned_verse_keys)
      ? data.learned_verse_keys
      : [];
    const relearningVerseKeys = Array.isArray(data?.relearning_verse_keys)
      ? data.relearning_verse_keys
      : [];
    const forgetAtByKey = data?.forget_at_by_key || {};
    learnedVerseKeys.forEach(verseKey => {
      if (!Number.isInteger(verseKey)) {
        return;
      }
      try {
        const tuple = verseKeyToTuple(verseKey);
        statusByVerseKey.set(verseKey, normalizeMemory(tuple, {
          learned: true,
          forget_at: forgetAtByKey[String(verseKey)],
          updated_at: data?.updated_at,
        }, now));
      } catch (error) {
        return;
      }
    });
    relearningVerseKeys.forEach(verseKey => {
      if (!Number.isInteger(verseKey) || statusByVerseKey.has(verseKey)) {
        return;
      }
      try {
        const tuple = verseKeyToTuple(verseKey);
        statusByVerseKey.set(verseKey, {
          ...normalizeMemory(tuple, {
            learned: false,
            updated_at: data?.updated_at,
          }, now),
          relearning: true,
        });
      } catch (error) {
        return;
      }
    });
  });
  return statusByVerseKey;
}

export async function loadDeckMemoryIndexes(database, uid, tuples) {
  if (!database) {
    throw new TypeError('Firestore database is required');
  }
  if (typeof uid !== 'string' || !uid) {
    throw new TypeError('A signed-in user is required');
  }
  const bookNumbers = [...new Set(
    dedupeVerseTuples(tuples).map(([bookNumber]) => bookNumber)
  )].sort((first, second) => first - second);
  const snapshots = await Promise.all(bookNumbers.map(bookNumber => (
    readMemoryDataWithRetry(() => getDoc(doc(
      database,
      'users',
      uid,
      'memorization_index',
      getMemorizationIndexId(bookNumber)
    )))
  )));
  return snapshots
    .filter(snapshot => snapshot.exists())
    .map(snapshot => snapshot.data());
}

export async function loadDeckMemoryStatus(database, uid, tuples, now = Date.now()) {
  const indexDocuments = await loadDeckMemoryIndexes(database, uid, tuples);
  return buildMemoryStatusMap(indexDocuments, now);
}

export async function loadDeckDailyLearningCount(
  database,
  uid,
  deckId,
  dateString
) {
  if (!database) {
    throw new TypeError('Firestore database is required');
  }
  if (typeof uid !== 'string' || !uid) {
    throw new TypeError('A signed-in user is required');
  }
  if (typeof deckId !== 'string' || !deckId) {
    throw new TypeError('A deck ID is required');
  }
  if (typeof dateString !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    throw new TypeError('A local date in YYYY-MM-DD format is required');
  }
  const snapshot = await readMemoryDataWithRetry(() => getDoc(doc(
    database,
    'users',
    uid,
    'decks',
    deckId,
    'daily_learning',
    dateString
  )));
  if (!snapshot.exists()) {
    return 0;
  }
  const count = snapshot.data()?.new_verses_learned;
  if (!Number.isInteger(count) || count < 0) {
    throw new TypeError('Daily learning count must be a non-negative integer');
  }
  return count;
}

export function calculateDeckProgress(tuples, statusByVerseKey, now = Date.now()) {
  validateNow(now);
  return dedupeVerseTuples(tuples).reduce((progress, tuple) => {
    const verseKey = verseTupleToKey(tuple);
    const memory = normalizeMemory(
      tuple,
      getMemoryForKey(statusByVerseKey, verseKey),
      now
    );
    if (memory.learned) {
      progress.learned_count += 1;
      if (memory.forget_at !== null && memory.forget_at > now) {
        progress.retained_count += 1;
      }
    }
    return progress;
  }, {
    learned_count: 0,
    retained_count: 0,
  });
}

export function countNeverLearnedVerses(tuples, statusByVerseKey) {
  return dedupeVerseTuples(tuples).reduce((count, tuple) => {
    const memory = getMemoryForKey(
      statusByVerseKey,
      verseTupleToKey(tuple)
    );
    return (
      memory?.learned === true
      || isPendingRelearning(memory)
    ) ? count : count + 1;
  }, 0);
}

export function createMemorizationSessionQueue(
  tuples,
  statusByVerseKey,
  options,
  now = Date.now()
) {
  const {
    paceTarget,
    newVerseLimit = paceTarget,
    fillWithFutureReviews = true,
  } = Number.isInteger(options)
    ? { paceTarget: options }
    : (options || {});
  if (!Number.isInteger(paceTarget) || paceTarget < 0) {
    throw new RangeError('Pace target must be a non-negative integer');
  }
  if (!Number.isInteger(newVerseLimit) || newVerseLimit < 0) {
    throw new RangeError('New verse limit must be a non-negative integer');
  }
  validateNow(now);
  const entries = dedupeVerseTuples(tuples)
    .map(tuple => {
      const verseKey = verseTupleToKey(tuple);
      const indexedMemory = getMemoryForKey(statusByVerseKey, verseKey);
      return {
        tuple,
        relearning: isPendingRelearning(indexedMemory),
        memory: normalizeMemory(
          tuple,
          indexedMemory,
          now
        ),
      };
    })
    .sort((first, second) => (
      first.memory.verse_key - second.memory.verse_key
    ));

  const overdue = entries
    .filter(({ memory }) => (
      memory.learned
      && memory.forget_at !== null
      && memory.forget_at <= now
    ))
    .sort((first, second) => (
      first.memory.forget_at - second.memory.forget_at
      || first.memory.verse_key - second.memory.verse_key
    ));
  const relearning = entries
    .filter(({ relearning: pending }) => pending);
  const newEntries = entries
    .filter(({ memory, relearning: pending }) => !memory.learned && !pending)
    .slice(0, Math.min(paceTarget, newVerseLimit));
  const futureFillCount = Math.max(0, paceTarget - newEntries.length);
  const future = entries
    .filter(({ memory }) => (
      memory.learned
      && memory.forget_at !== null
      && memory.forget_at > now
    ))
    .sort((first, second) => (
      first.memory.forget_at - second.memory.forget_at
      || first.memory.verse_key - second.memory.verse_key
    ))
    .slice(0, fillWithFutureReviews ? futureFillCount : 0);

  return [
    ...overdue.map(({ tuple, memory }) => (
      createQueueCard(tuple, memory, 'recall', now, memory.forget_at)
    )),
    ...relearning.map(({ tuple, memory }) => (
      createQueueCard(tuple, memory, 'learn', now, null, true)
    )),
    ...newEntries.map(({ tuple, memory }) => (
      createQueueCard(tuple, memory, 'learn', now)
    )),
    ...future.map(({ tuple, memory }) => (
      createQueueCard(tuple, memory, 'recall', now, memory.forget_at)
    )),
  ];
}

export function getRecallIntervalAnchor(memory) {
  if (memory.last_forgot_at !== null && memory.last_correct_at !== null) {
    return Math.max(memory.last_forgot_at, memory.last_correct_at);
  }
  return memory.last_forgot_at !== null
    ? memory.last_forgot_at
    : memory.last_correct_at;
}

export function applyMemoryRating(tuple, memory, rating, now = Date.now()) {
  validateNow(now);
  const current = normalizeMemory(tuple, memory, now);
  if (Object.prototype.hasOwnProperty.call(MEMORY_RATING_MULTIPLIERS, rating)) {
    if (current.last_correct_at === null) {
      throw new TypeError('A successful recall requires a previous correct time');
    }
    const interval = (
      now - getRecallIntervalAnchor(current)
    ) * MEMORY_RATING_MULTIPLIERS[rating];
    return {
      ...current,
      learned: true,
      last_correct_at: now,
      last_forgot_at: null,
      forget_at: now + interval,
      correct_count: current.correct_count + 1,
      updated_at: now,
    };
  }
  if (rating === 'forgot') {
    return {
      ...current,
      learned: true,
      last_forgot_at: now,
      forget_at: now + MEMORY_RECALL_DELAY_MS,
      updated_at: now,
    };
  }
  if (rating === 'forgot_learn_again') {
    return {
      ...current,
      learned: false,
      last_forgot_at: now,
      forget_at: null,
      updated_at: now,
    };
  }
  throw new RangeError(`Unknown memorization rating: ${rating}`);
}

export function confirmMemoryLearned(tuple, memory, now = Date.now()) {
  validateNow(now);
  const current = normalizeMemory(tuple, memory, now);
  return {
    ...current,
    learned: true,
    last_correct_at: now,
    last_forgot_at: null,
    forget_at: now + MEMORY_RECALL_DELAY_MS,
    updated_at: now,
  };
}

export function removeQueueCard(queue, verseKey) {
  return queue.filter(card => card.verse_key !== verseKey);
}

export function requeueCardByDueTime(queue, card, dueAt) {
  if (!Number.isFinite(dueAt)) {
    throw new TypeError('Queue due time must be milliseconds');
  }
  return [
    ...removeQueueCard(queue, card.verse_key),
    {
      ...card,
      mode: 'recall',
      available_at: dueAt,
      scheduled_for: dueAt,
    },
  ].sort(compareQueueCards);
}

export function requeueCardForImmediateLearn(queue, card, now = Date.now()) {
  validateNow(now);
  return [{
    ...card,
    mode: 'learn',
    available_at: now,
    scheduled_for: null,
  }, ...removeQueueCard(queue, card.verse_key)];
}

export function updateQueueAfterMemoryAction(
  queue,
  card,
  updatedMemory,
  action,
  now = Date.now()
) {
  validateNow(now);
  const updatedCard = {
    ...card,
    memory: updatedMemory,
  };
  if (action === 'hard' || action === 'okay' || action === 'easy') {
    return removeQueueCard(queue, card.verse_key);
  }
  if (action === 'forgot') {
    return requeueCardByDueTime(
      queue,
      updatedCard,
      updatedMemory.forget_at
    );
  }
  if (action === 'forgot_learn_again') {
    return requeueCardForImmediateLearn(queue, updatedCard, now);
  }
  if (action === 'confirm') {
    return requeueCardByDueTime(
      queue,
      updatedCard,
      updatedMemory.forget_at
    );
  }
  throw new RangeError(`Unknown memory queue action: ${action}`);
}

export function getLearningStageIndex(stage) {
  const index = MEMORY_LEARNING_STAGES.indexOf(stage);
  if (index === -1) {
    throw new RangeError(`Unknown memory learning stage: ${stage}`);
  }
  return index;
}

export function getNextLearningStage(stage) {
  const index = getLearningStageIndex(stage);
  return MEMORY_LEARNING_STAGES[Math.min(
    MEMORY_LEARNING_STAGES.length - 1,
    index + 1
  )];
}

export function getPreviousLearningStage(stage) {
  const index = getLearningStageIndex(stage);
  return MEMORY_LEARNING_STAGES[Math.max(0, index - 1)];
}

export function getNextQueueWakeMs(queue, now = Date.now()) {
  validateNow(now);
  if (queue.length === 0) {
    return null;
  }
  return Math.max(
    0,
    Math.min(...queue.map(card => card.available_at)) - now
  );
}

export async function loadActiveCardMemory(
  database,
  uid,
  tuple,
  statusByVerseKey,
  now = Date.now()
) {
  if (!database) {
    throw new TypeError('Firestore database is required');
  }
  if (typeof uid !== 'string' || !uid) {
    throw new TypeError('A signed-in user is required');
  }
  const verseKey = verseTupleToKey(tuple);
  const snapshot = await readMemoryDataWithRetry(() => getDoc(doc(
    database,
    'users',
    uid,
    'memorization',
    String(verseKey)
  )));
  const indexedMemory = getMemoryForKey(statusByVerseKey, verseKey) || {};
  const persistedMemory = snapshot.exists() ? snapshot.data() : {};
  return normalizeMemory(tuple, {
    ...indexedMemory,
    ...persistedMemory,
  }, now);
}

function buildMemoryIndexUpdate(memory) {
  const verseKey = memory.verse_key;
  return {
    book_number: memory.book_number,
    learned_verse_keys: memory.learned
      ? arrayUnion(verseKey)
      : arrayRemove(verseKey),
    relearning_verse_keys: memory.learned
      ? arrayRemove(verseKey)
      : (
        memory.last_correct_at === null
          ? arrayRemove(verseKey)
          : arrayUnion(verseKey)
      ),
    forget_at_by_key: {
      [String(verseKey)]: memory.learned ? memory.forget_at : null,
    },
    updated_at: memory.updated_at,
  };
}

export function applyMemoryAction(tuple, memory, action, now) {
  return action === 'confirm'
    ? confirmMemoryLearned(tuple, memory, now)
    : applyMemoryRating(tuple, memory, action, now);
}

export function isFirstTimeLearned(memory, action) {
  return action === 'confirm'
    && memory.learned === false
    && memory.last_correct_at === null;
}

/**
 * Apply one memorization action inside a transaction so that concurrent
 * sessions cannot lose recall counts or double count daily learning. Deck
 * progress aggregates are derived by trusted triggers from the written
 * memorization index.
 */
export async function commitMemoryAction(
  database,
  uid,
  deckId,
  tuple,
  action,
  options = {}
) {
  if (!database) {
    throw new TypeError('Firestore database is required');
  }
  if (typeof uid !== 'string' || !uid) {
    throw new TypeError('A signed-in user is required');
  }
  if (typeof deckId !== 'string' || !deckId) {
    throw new TypeError('A deck ID is required');
  }
  const { dailyLearningDate = null } = options;
  if (
    typeof dailyLearningDate !== 'string'
    || !/^\d{4}-\d{2}-\d{2}$/.test(dailyLearningDate)
  ) {
    throw new TypeError('A local daily learning date is required');
  }
  const verseKey = verseTupleToKey(tuple);
  const memoryReference = doc(
    database,
    'users',
    uid,
    'memorization',
    String(verseKey)
  );
  const dailyLearningReference = doc(
    database,
    'users',
    uid,
    'decks',
    deckId,
    'daily_learning',
    dailyLearningDate
  );
  return runTransaction(database, async transaction => {
    const now = Date.now();
    const [memorySnapshot, dailyLearningSnapshot] = await Promise.all([
      transaction.get(memoryReference),
      transaction.get(dailyLearningReference),
    ]);
    const currentMemory = normalizeMemory(
      tuple,
      memorySnapshot.exists() ? memorySnapshot.data() : {},
      now
    );
    const memory = applyMemoryAction(tuple, currentMemory, action, now);
    const countedAsNewToday = isFirstTimeLearned(currentMemory, action);
    const storedCount = dailyLearningSnapshot.exists()
      ? dailyLearningSnapshot.data()?.new_verses_learned
      : 0;
    const dailyLearningCount = (
      Number.isInteger(storedCount) && storedCount >= 0 ? storedCount : 0
    ) + (countedAsNewToday ? 1 : 0);
    transaction.set(memoryReference, memory);
    transaction.set(
      doc(
        database,
        'users',
        uid,
        'memorization_index',
        getMemorizationIndexId(memory.book_number)
      ),
      buildMemoryIndexUpdate(memory),
      { merge: true }
    );
    if (countedAsNewToday) {
      transaction.set(
        dailyLearningReference,
        {
          new_verses_learned: dailyLearningCount,
          updated_at: now,
        },
        { merge: true }
      );
    }
    return { memory, dailyLearningCount, countedAsNewToday };
  });
}
